<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">
      <div class="feature-products">
        <BaseFilter
          screenKey="customer-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>

      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Customer List</h3>
          <div class="table-tools d-flex">
            <button class="btn btn-sm btn-outline-secondary me-2" @click="fetchData">
              <i class="fa fa-refresh me-1"></i> Refresh
            </button>

            <button v-if="canManageCustomers" class="btn btn-sm btn-primary" @click="toggleForm">
              <i class="fa fa-plus"></i> Add
            </button>
          </div>
        </div>
        <BaseTable
          storage-key="customers"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(status)="{ item }">
            <span :class="item.status === 'Active' ? 'badge bg-success' : 'badge bg-secondary'">
              {{ item.status }}
            </span>
          </template>
          <template #cell(rank_id)="{ item }">
            <span v-if="item.customerRank" class="badge fw-semibold" :style="getRankBadgeStyle(item.customerRank.id)">
              {{ item.customerRank.name }}
            </span>
            <span v-else class="text-muted small">No Rank</span>
          </template>

          <template #cell(balance)="{ item }">
            <span :class="Number(item.balance) < 0 ? 'text-danger fw-semibold' : 'text-success fw-semibold'">
              {{ Number(item.balance || 0).toLocaleString() }}
            </span>
          </template>

          <template #actions="{ item }">
            <button v-if="canManageCustomers" class="btn btn-sm btn-primary me-1" @click="editData(item)">
              <i class="fa fa-edit"></i>
            </button>
            <button class="btn btn-sm btn-outline-info me-1" title="Upload order" @click="openImportOrder(item)">
              <i class="fa fa-upload"></i>
            </button>
          </template>
        </BaseTable>
      </div>
    </div>
    <ImportOrder
      :isOpen="!!importCustomer"
      :customer="importCustomer"
      @close="importCustomer = null"
    />
    <Form
      :isOpen="isShowForm"
      :itemData="selectedItem"
      :ranks="ranks"
      @close="isShowForm = false"
      @save="handleSaveData"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import { useUser } from "@/composables/useUser";
import { useCustomerRank } from "@/composables/useCustomerRank";
import Form from './Form.vue'
import ImportOrder from './ImportOrderCustomer.vue'
import { createToaster } from "@meforma/vue-toaster";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { getUsersApi, createUserApi, updateUserApi } = useUser();
const { getCustomerRanksApi } = useCustomerRank();

const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const ranks = ref([]);
const total = ref(0);
const isShowForm = ref(false)
const selectedItem = ref(null);
const canManageCustomers = computed(() => localStorage.getItem('user_role')?.toLowerCase() !== 'operation');

const allParams = reactive({
  // Phân trang & Sort
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  // Lọc dữ liệu
  searchFields: '',
  search: 'role_id:2',
  include: 'customerRank',
});

// Cấu hình các ô nhập của Filter
const filterConfig = [
  {
    key: 'name',
    type: 'text',
    label: 'Name',
    operator: 'like',
    placeholder: 'Name...',
    isDefault: true
  },
  {
    key: 'email',
    type: 'text',
    label: 'Email',
    operator: 'like',
    placeholder: 'Email...',
    isDefault: true
  }
];

// Cấu hình các cột của Bảng
const tableCols = [
  { key: 'id', label: 'ID', width: '70px', sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'phone', label: 'Phone', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'rank_id', label: 'Rank', width: '150px' },
  { key: 'balance', label: 'Balance', width: '140px' },
];

// --- PHẦN LOGIC (HÀNH ĐỘNG) ---
// role_id:2 luôn luôn được giữ cố định để chỉ lấy Customer
const CUSTOMER_FIXED_SEARCH = 'role_id:2';

const fetchData = async (resetPage = false) => {
    if (resetPage) {
        allParams.page = 1;
    }

    loading.value = true;

    try {
        // Luôn đảm bảo role_id:2 có trong search
        const params = { ...allParams };
        if (params.search && params.search.trim()) {
            // Nếu đã có search term khác từ filter, thêm vào trước
            if (!params.search.includes(CUSTOMER_FIXED_SEARCH)) {
                params.search = CUSTOMER_FIXED_SEARCH + ';' + params.search;
            }
        } else {
            params.search = CUSTOMER_FIXED_SEARCH;
        }

        const res = await getUsersApi(params);

        if (res.success && res.data) {
            tableData.value = res.data.data || [];

            total.value = res.data.meta?.pagination?.total || 0;
        } else {
            toaster.error(res.error || "Không thể tải dữ liệu");
        }
    } catch (err) {
        toaster.error("Có lỗi xảy ra trong quá trình kết nối");
    } finally {
        loading.value = false;
    }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

// Reset bộ lọc - luôn giữ search rỗng vì fetchData tự inject role_id:2
const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';
  
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

// Xử lý khi bấm vào tiêu đề cột để sắp xếp
const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData(true);
};

const handleSelection = (ids) => {
  console.log("IDs đã chọn:", ids);
};

const toggleForm = () => {
  selectedItem.value = null;
  isShowForm.value = true;
};
const handleSaveData = async (data) => {
    try {
        let res;
        const payload = {
            name: data.name,
            email: data.email,
            phone: data.phone,
            rank_id: data.rank_id,
            role_id: 2, // Customer luôn là role_id 2
            additional_data: {
              auto_buy_tracking_flg: data.auto_buy_tracking_flg,
              auto_pay_order_flg: data.auto_pay_order_flg,
            },
        };
        if (data.password) {
            payload.password = data.password;
        }
        // Admin gán/bỏ gán Sale phụ trách cho customer (rỗng = bỏ gán → null)
        if ('sale_id' in data) {
            payload.sale_id = data.sale_id || null;
        }

        if (data.id) {
            res = await updateUserApi(data.id, payload);
        } else {
            res = await createUserApi(payload);
        }

        if (res.success) {
            toaster.success(data.id ? "Cập nhật thành công!" : "Thêm mới thành công!");
            isShowForm.value = false;
            fetchData();
        } else {
            toaster.error(res.error || "Thao tác thất bại");
        }
    } catch (err) {
        toaster.error("Có lỗi xảy ra trong quá trình kết nối");
    }
};
const RANK_COLORS = [
  { bg: '#e8f4fd', text: '#1565c0' },  // blue
  { bg: '#fce4ec', text: '#c62828' },  // red
  { bg: '#e8f5e9', text: '#2e7d32' },  // green
  { bg: '#fff8e1', text: '#f57f17' },  // amber
  { bg: '#f3e5f5', text: '#6a1b9a' },  // purple
  { bg: '#e0f2f1', text: '#00695c' },  // teal
  { bg: '#fbe9e7', text: '#bf360c' },  // deep orange
  { bg: '#e8eaf6', text: '#283593' },  // indigo
  { bg: '#fafafa', text: '#37474f' },  // blue grey
  { bg: '#f9fbe7', text: '#558b2f' },  // light green
];

const getRankBadgeClass = (rankId) => {
  const idx = ((rankId - 1) % RANK_COLORS.length + RANK_COLORS.length) % RANK_COLORS.length;
  const color = RANK_COLORS[idx];
  return {
    badge: true,
    'fw-semibold': true,
    [`rank-badge-${idx}`]: true,
  };
};

const getRankBadgeStyle = (rankId) => {
  const idx = ((rankId - 1) % RANK_COLORS.length + RANK_COLORS.length) % RANK_COLORS.length;
  const color = RANK_COLORS[idx];
  return `background-color: ${color.bg}; color: ${color.text};`;
};

const fetchRanks = async () => {
    try {
        const res = await getCustomerRanksApi({ limit: -1 });
        if (res.success && res.data) {
            ranks.value = res.data.data || [];
        }
    } catch (err) {
        console.error('Lỗi lấy danh sách rank:', err);
    }
};

watch(() => [allParams.page, allParams.limit], () => {
    fetchData();
});

onMounted(() => {
    fetchData();
    fetchRanks();
});

const editData = (item) => {
  selectedItem.value = { ...item };
  isShowForm.value = true;
};

// Admin upload order hộ khách hàng
const importCustomer = ref(null);
const openImportOrder = (item) => {
  importCustomer.value = { id: item.id, name: item.name, email: item.email };
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
</style>
